'use client';

import { useState, useMemo, useRef } from 'react';
import { ChevronDown, Coins, Search, ExternalLink } from 'lucide-react';
import { useVirtualizer, VirtualItem } from '@tanstack/react-virtual';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from 'src/components/ui/avatar';
import { EVMBalanceItem } from 'src/api/blockchain';
import { MongoLong, numberWithCommas } from 'src/utils/number';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from 'src/components/ui/popover';
import { Link, useSearchParams } from 'react-router-dom';

const formatBalance = (balance: number | MongoLong): string => {
  const num = typeof balance === 'number' ? balance : Number(balance);
  if (num === 0) return '0';
  if (num < 0.000001) return num.toExponential(2);
  if (num < 1) return num.toFixed(6);
  if (num < 1000) return num.toFixed(4);
  if (num < 1000000) return num.toFixed(2);
  return num.toExponential(2);
};

interface TokenHoldingsDropdownProps {
  tokens: EVMBalanceItem[];
}

export const TokenHoldingsDropdown = ({
  tokens,
}: TokenHoldingsDropdownProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [searchParams] = useSearchParams();

  // Filter out ETH tokens
  const nonEthTokens = useMemo(() => {
    return tokens.filter((token) => token.symbol !== 'ETH');
  }, [tokens]);

  const filteredTokens = useMemo(() => {
    if (!searchTerm) return nonEthTokens;
    return nonEthTokens.filter(
      (t: EVMBalanceItem) =>
        t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.symbol.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [nonEthTokens, searchTerm]);

  const totalValue = useMemo(() => {
    const tokensWithPrices = nonEthTokens.filter(
      (token) => token.total_value_usd != null && token.total_value_usd > 0,
    );
    return tokensWithPrices.reduce(
      (sum, token) => sum + token.total_value_usd,
      0,
    );
  }, [nonEthTokens]);

  const hasAnyPrices = useMemo(() => {
    return nonEthTokens.some(
      (token) => token.total_value_usd != null && token.total_value_usd > 0,
    );
  }, [nonEthTokens]);

  // Create URL for token balances tab
  const tokenBalancesUrl = useMemo(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', 'token-balances');
    return `?${newSearchParams.toString()}`;
  }, [searchParams]);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="h-12 w-full min-w-[26rem] justify-between text-base"
        >
          <div className="flex items-center">
            <span className="font-semibold">
              {hasAnyPrices
                ? `$${numberWithCommas(totalValue, 2)}`
                : 'Token Holdings'}
            </span>
            <span className="ml-2 text-xs text-muted-foreground">
              ({nonEthTokens.length} Tokens)
            </span>
          </div>
          <div className="flex items-center">
            <Coins className="mr-2 h-5 w-5 text-muted-foreground" />
            <ChevronDown
              className={`h-5 w-5 transition-transform ${
                isOpen ? 'rotate-180' : ''
              }`}
            />
          </div>
        </Button>
      </PopoverTrigger>

      <PopoverContent
        side="bottom"
        align="start"
        sideOffset={8}
        className="w-[600px] p-4 shadow-xl"
        avoidCollisions={false}
      >
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search for Token Name"
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <h4 className="mb-3 rounded-lg bg-muted px-3 py-1 text-xs font-semibold uppercase text-muted-foreground">
          ERC-20 Tokens ({filteredTokens.length})
        </h4>

        {filteredTokens.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            {searchTerm
              ? 'No tokens found matching your search.'
              : 'No ERC-20 tokens found.'}
          </div>
        ) : (
          <TokenListVirtualized filteredTokens={filteredTokens} />
        )}

        <div className="mt-4">
          <Link
            to={tokenBalancesUrl}
            className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-sm font-medium text-primary transition-colors hover:border-primary/30 hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            onClick={() => setIsOpen(false)}
          >
            <ExternalLink className="mb-[1px] h-4 w-4" />
            View All Holdings
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const TokenListVirtualized = ({
  filteredTokens,
}: {
  filteredTokens: EVMBalanceItem[];
}) => {
  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: filteredTokens.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 80,
    overscan: 5,
  });

  return (
    <div ref={parentRef} className="h-[400px] w-full overflow-auto">
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow: VirtualItem) => {
          const token = filteredTokens[virtualRow.index];
          return (
            <div
              key={virtualRow.key}
              className="absolute inset-x-0"
              style={{
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <div className="mx-1 flex items-center justify-between rounded-lg border border-border p-3 hover:bg-muted/50">
                <div className="flex items-center gap-3">
                  <Avatar className="h-5 w-5">
                    {token.logo && (
                      <AvatarImage src={token.logo} alt={token.symbol} />
                    )}
                    <AvatarFallback className="text-sm font-medium">
                      {token.symbol
                        ? token.symbol.substring(0, 2).toUpperCase()
                        : '?'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="text-sm font-medium">
                      {token.name || token.token_address}
                      {token.symbol && ` (${token.symbol})`}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {token.actual_balance != null
                        ? formatBalance(token.actual_balance)
                        : `${formatBalance(token.raw_balance)} (raw)`}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium">
                    {token.total_value_usd != null && token.total_value_usd > 0
                      ? `$${numberWithCommas(token.total_value_usd, 2)}`
                      : ''}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {token.price_usd != null && token.price_usd > 0
                      ? `@$${numberWithCommas(token.price_usd, 4)}`
                      : ''}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
